import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Loading = () => {
    // Detect system theme preference since this renders before ThemeProvider
    const [isDark, setIsDark] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return true; // Default to dark
    });
    const [dots, setDots] = useState('');
    const [loadingText, setLoadingText] = useState('');
    const fullText = '> initializing';

    // Typewriter effect for loading text
    useEffect(() => {
        if (loadingText.length < fullText.length) {
            const timeout = setTimeout(() => {
                setLoadingText(fullText.slice(0, loadingText.length + 1));
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [loadingText]);

    // Animated dots
    useEffect(() => {
        const interval = setInterval(() => {
            setDots(prev => prev.length >= 3 ? '' : prev + '.');
        }, 500);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className={`min-h-screen flex items-center justify-center transition-colors ${
            isDark ? 'bg-dark-bg-primary' : 'bg-light-bg-primary'
        }`}>
            <div className="flex flex-col items-center gap-8">
                {/* Terminal Loading Animation */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className={`font-mono text-2xl md:text-3xl ${
                        isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                    }`}
                >
                    <span className={isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'}>
                        {loadingText}
                    </span>
                    <span className={isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'}>
                        {dots}
                    </span>
                    <motion.span
                        animate={{ opacity: [1, 0, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className={isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'}
                    >
                        _
                    </motion.span>
                </motion.div>

                {/* Progress Bar */}
                <div className={`w-64 md:w-96 h-2 rounded-full overflow-hidden ${
                    isDark ? 'bg-dark-bg-secondary' : 'bg-light-bg-secondary'
                }`}>
                    <motion.div
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        className={`h-full ${
                            isDark ? 'bg-dark-brand-primary' : 'bg-light-brand-primary'
                        }`}
                        style={{
                            boxShadow: isDark
                                ? '0 0 10px rgba(0, 255, 65, 0.5)'
                                : '0 0 10px rgba(9, 105, 218, 0.3)'
                        }}
                    />
                </div>

                {/* Loading Messages */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className={`font-mono text-xs md:text-sm ${
                        isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                    }`}
                >
                    <p className="flex items-center gap-2">
                        <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                            [✓]
                        </span>
                        Loading portfolio data
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default Loading;
